import {
  Alert,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  fetchSlaPreferences,
  patchSlaPreferences,
  toSlaApiError,
  type SlaApiError,
} from "api";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import type { SlaUserPreferencesDto } from "model/slaModel";
import { useCallback, useEffect, useState } from "react";

/**
 * **Preferenze individuali SLA** (piano §5 F4c: "saluto e digest come preferenze
 * individuali").
 *
 * Rotta **realmente esistente** lato Server, per l'utente autenticato:
 * `GET|PATCH /sla/preferences` (`Controller.SLA.pas:99-105`,
 * `MVCDoc('Preferenze digest e saluto dell''utente autenticato')`). L'adattatore
 * client esiste gia' (`slaApi.ts:404-441`) e **non ha consumatori**: questo pannello
 * e' il primo. Nessuna rotta nuova e nessun `iduser` nel payload — l'utente lo
 * identifica il JWT.
 *
 * **Cosa sono le due preferenze rese** (le sole del criterio F4c):
 * - **digest** — `digestenabled` del DTO. L'**ora** del digest non e' una preferenza
 *   individuale: `SlaUserPreferencesDto` non la porta e la colonna `digest_at` sta in
 *   `sla_settings` (organizzazione, `Service.SLA.pas:567`). La chiave
 *   `sla.preferences.digest.at` (parametro `{time}`) resta quindi **non resa**: senza
 *   un dato per utente il pannello non ha un valore da interpolare e non ne inventa
 *   uno (vedi il report: chiave del catalogo senza campo nel contratto);
 * - **saluto** — `greetingdismissedat` del DTO: la chiave riusata e' quella gia'
 *   esistente di F3c (`sla.activities.greeting.dismiss`) e la scrittura manda
 *   `greeting_dismissed`. La chiusura e' **una sola** (`slaModel.ts:409-417`: il
 *   Server registra solo la chiusura), quindi a saluto gia' chiuso l'interruttore e'
 *   disabilitato invece di offrire un comando che il contratto non accetta.
 *
 * **Fuori ambito F4c**: `escalationenabled`/`transactionalenabled` sono nel DTO
 * (recezione di contratto della variazione (d), piano §14.3) ma non sono preferenze
 * chieste dal criterio: restano non rese, e le chiavi
 * `sla.preferences.escalation.enabled|transactional.enabled` non vengono usate qui.
 *
 * **Nessun cambio a insaputa dell'utente** (regola di F4b, §5): l'apertura del
 * pannello **legge** e basta; la scrittura avviene solo su azione esplicita
 * dell'utente e il valore mostrato e' sempre quello **riletto** dal Server
 * (`data-preferences-source`), mai l'intenzione del click.
 */
interface SlaUserPreferencesPanelProps {
  idorganization: number;
}

const TEST_ID = "sla-statistics-preferences";

const SlaUserPreferencesPanel = ({ idorganization }: SlaUserPreferencesPanelProps) => {
  const translations = useTranslations();
  const [preferences, setPreferences] = useState<SlaUserPreferencesDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<SlaApiError | null>(null);

  const t = useCallback(
    (key: string): string => getTranslation(translations, key),
    [translations],
  );

  /** Lettura: nessuna scrittura implicita, nessun effetto collaterale. */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchSlaPreferences(idorganization);
      setPreferences(response);
      setError(null);
    } catch (caught) {
      setPreferences(null);
      setError(toSlaApiError(caught));
    } finally {
      setLoading(false);
    }
  }, [idorganization]);

  useEffect(() => {
    load();
  }, [load]);

  /** Scrittura esplicita di un solo campo, poi rilettura. */
  const patch = async (body: Parameters<typeof patchSlaPreferences>[1]) => {
    setLoading(true);
    try {
      await patchSlaPreferences(idorganization, body);
      setError(null);
    } catch (caught) {
      setError(toSlaApiError(caught));
    } finally {
      await load();
    }
  };

  const greetingDismissed = preferences?.greetingdismissedat !== null &&
    preferences?.greetingdismissedat !== undefined;

  return (
    <Stack
      spacing={1}
      data-testid={`${TEST_ID}-panel`}
      data-preferences-source="sla/preferences"
      data-digest-enabled={preferences ? String(preferences.digestenabled) : ""}
      data-greeting-dismissed={preferences ? String(greetingDismissed) : ""}
    >
      <Typography variant="subtitle1" data-testid={`${TEST_ID}-title`}>
        {t("sla.preferences.title")}
      </Typography>

      {loading && <CircularProgress size={20} data-testid={`${TEST_ID}-loading`} />}

      {error && (
        <Alert
          severity="error"
          data-testid={`${TEST_ID}-error`}
          data-error-status={error.status === null ? "" : String(error.status)}
        >
          {t(
            error.status === 403
              ? SLA_MONITOR_TEXT_KEYS.forbidden
              : SLA_MONITOR_TEXT_KEYS.loadError,
          )}
        </Alert>
      )}

      {preferences && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={preferences.digestenabled}
                onChange={({ target }) => patch({ digest_enabled: target.checked })}
                inputProps={{
                  "aria-label": t("sla.preferences.digest.enabled"),
                  // Vedi `SlaActivitiesSurface.tsx:236-241`: `inputProps` tipizza
                  // gli attributi HTML standard, il testid va passato senza perdere
                  // il tipo.
                  ...({ "data-testid": `${TEST_ID}-digest` } as Record<string, string>),
                }}
              />
            }
            label={
              <Typography variant="body2" data-testid={`${TEST_ID}-digest-label`}>
                {t("sla.preferences.digest.enabled")}
              </Typography>
            }
          />
          <FormControlLabel
            disabled={greetingDismissed}
            control={
              <Switch
                checked={greetingDismissed}
                // Il saluto si chiude una volta sola: nessun ritorno indietro.
                onChange={() => patch({ greeting_dismissed: true })}
                inputProps={{
                  "aria-label": t("sla.activities.greeting.dismiss"),
                  ...({ "data-testid": `${TEST_ID}-greeting` } as Record<string, string>),
                }}
              />
            }
            label={
              <Typography variant="body2" data-testid={`${TEST_ID}-greeting-label`}>
                {t("sla.activities.greeting.dismiss")}
              </Typography>
            }
          />
        </>
      )}
    </Stack>
  );
};

export default SlaUserPreferencesPanel;
